import {useState} from "react"
import {apiRooms} from "./Api"
import {layoutToWalls} from "../Helper/Wall"
import {useRefState} from "./RefStateHook"
import {v4 as uuidv4} from "uuid"
import {roomSetupFireProtectionClassInit, roomSetupShapeDimensionMax, roomSetupShapeInit, roomSetupTypeInit} from "../settings"

const initialState = {
    id: null,
    type: roomSetupTypeInit,
    shape: roomSetupShapeInit, // should be change solution
    interiorBasicMode: false,
    useCeiling: false,
    ceilingConstitution: null,
    wallConstitution: null,
    floorConstitution: null,
    dimensions: {}, // should be change solution
    interiors: [], // should be change solution
    noises: [], // should be change solution
    customBudget: false,
    budget: null,
    fireProtectionClass: roomSetupFireProtectionClassInit,
    preferredImpressions: [],
}

export const useRoomSetup = (local, messenger) => {
    const state = useRefState(initialState, 'room')
    const [cmp, setCmp] = useState(() => state.getCurrent())
    const [handleState, setHandleState] = useState(null)

    const setStateAndSave = (newState) => {
        if (JSON.stringify(state.getCurrent()) === JSON.stringify({...state.getCurrent(), ...newState})) {
            return
        }

        state.setState({...state.getCurrent(), ...newState, id: newState?.id})
    }

    const save = async () => {
        try {
            const interiors = state.getCurrent().interiors.filter(interior => interior.position.x !== null && interior.position.y !== null && interior.position.z !== null)
            const response = await apiRooms(roomSetupApiData({...state.getCurrent(), interiors}), state.getCurrent().id)
            setStateAndSave({id: response.id, interiors})
            setCmp({...state.getCurrent(), id: response.id, interiors})
        } catch (error) {
            messenger.showError(local.__(error.message))
        }
    }

    const hasDiff = () => {
        return JSON.stringify(cmp) !== JSON.stringify(state.getCurrent())
    }

    const reset = () => {
        setStateAndSave(initialState)
    }

    const getId = () => {
        return state.getCurrent().id
    }

    const setRoom = (room) => {
        setStateAndSave(room)
    }

    const getType = () => state.getCurrent().type
    const setType = (type) => setStateAndSave({type})
    const getShape = () => state.getCurrent().shape
    const setShape = (shape) => setStateAndSave({shape, interiors: []})
    const hasDimensions = () => Object.values(state.getCurrent().dimensions).length > 0
    const getDimensions = () => state.getCurrent().dimensions
    const setDimensions = (dimensions) => setStateAndSave({dimensions, interiors: []})
    const setDimension = (dimension, value) => setStateAndSave({dimensions: {...state.getCurrent().dimensions, [dimension]: parseInt(value)}, interiors: []})
    const getRoomHeight = () => state.getCurrent().dimensions.height
    const getWalls = () => layoutToWalls(getShape(), getDimensions())
    const isInteriorBasicMode = () => state.getCurrent().interiorBasicMode
    const unsetInteriorBasicMode = () => setStateAndSave({interiorBasicMode: false})
    const setInteriorBasicMode = () => setStateAndSave({interiorBasicMode: true})
    const isCeilingUsable = () => state.getCurrent().useCeiling
    const toggleCeilingUsable = () => setStateAndSave({useCeiling: !state.getCurrent().useCeiling})
    const getCeilingConstitution = () => state.getCurrent().ceilingConstitution
    const setCeilingConstitution = ceilingConstitution => setStateAndSave({ceilingConstitution})
    const getWallConstitution = () => state.getCurrent().wallConstitution
    const setWallConstitution = wallConstitution => setStateAndSave({wallConstitution})
    const getFloorConstitution = () => state.getCurrent().floorConstitution
    const setFloorConstitution = floorConstitution => setStateAndSave({floorConstitution})
    const getNoises = () => state.getCurrent().noises
    const hasNoise = (noise) => state.getCurrent().noises.includes(noise)
    const toggleNoise = (noise) => setStateAndSave(hasNoise(noise) ? {noises: state.getCurrent().noises.filter(cNoise => cNoise !== noise)} : {noises: [...state.getCurrent().noises, noise]})
    const getImpressions = () => state.getCurrent().preferredImpressions
    const hasImpression = (preferredImpression) => state.getCurrent().preferredImpressions.includes(preferredImpression)
    const toggleImpression = (preferredImpression) => setStateAndSave(hasImpression(preferredImpression) ? {preferredImpressions: state.getCurrent().preferredImpressions.filter(cPreferredImpression => cPreferredImpression !== preferredImpression)} : {preferredImpressions: [...state.getCurrent().preferredImpressions, preferredImpression]})
    const getFireProtectionClass = () => state.getCurrent().fireProtectionClass
    const setFireProtectionClass = (fireProtectionClass) => setStateAndSave({fireProtectionClass})
    const isCustomBudget = () => state.getCurrent().customBudget
    const setCustomBudget = () => {
        if (state.getCurrent().customBudget === false) {
            setStateAndSave({customBudget: true, budget: null})
        }
    }
    const unsetCustomBudget = () => setStateAndSave({customBudget: false})
    const getBudget = () => state.getCurrent().budget
    const setBudget = budget => setStateAndSave({budget: budget})
    const getInteriors = () => state.getCurrent().interiors
    const addInterior = (rawItem) => {
        if(hasMissingPositionForInteriors()) {
            return
        }
        const item = convertToInterior(rawItem)
        setHandle(item.id)
        setStateAndSave({interiors: [...state.getCurrent().interiors, item]})
    }

    const hasMissingPositionForInteriors = () => {
        const interiors = state.getCurrent().interiors
        for (let i = 0; i < interiors.length; i++) {
            if (interiors[i].position.x === null || interiors[i].position.y === null || interiors[i].position.z === null) {
                return true
            }
        }

        return false
    }

    const hasHandle = () => {
        return handleState !== null
    }

    const getHandle = () => {
        return handleState
    }

    const itemGet = () => {
        return state.getCurrent().interiors.find(item => item.id === getHandle())
    }

    const setHandle = (id) => {
        setHandleState(id)
    }

    const unsetHandle = () => {
        setHandleState(null)
    }

    const itemGetProperty = (property) => {
        return itemGet()?.[property]
    }

    const itemSetProperty = (props) => {
        const items = [...state.getCurrent().interiors]
        const itemIndex = items.findIndex(item => item.id === getHandle())

        if (itemIndex >= 0) {
            items[itemIndex] = {...items[itemIndex], ...props}
            setStateAndSave({interiors: items})
        }
    }

    const itemAllowUserInput = (property) => {
        return itemGetProperty("userInput")?.includes(property)
    }

    const itemSetPosition = (x, y, z, wallIndex = null) => {
        if (wallIndex) {
            itemSetProperty({position: {x, y, z}, wall: wallIndex})
        } else {
            itemSetProperty({position: {x, y, z}})
        }
    }

    const itemSetSize = (width, height, depth) => {
        itemSetProperty({
            width: parseInt(width),
            height: parseInt(height),
            depth: parseInt(depth),
        })
    }

    const itemSetWidth = (width) => {
        itemSetProperty({width: parseInt(width)})
    }

    const itemSetHeight = (height) => {
        itemSetProperty({height: parseInt(height)})
    }

    const itemSetDepth = (depth) => {
        itemSetProperty({depth: parseInt(depth)})
    }

    const itemSetOffset = (offset) => {
        itemSetProperty({
            position: {...itemGetProperty("position"), y: parseInt(offset)}
        })
    }

    const itemMoveToNextWall = () => {
        const wall = (itemGetProperty("wall") + 1) < (getWalls().length)
            ? (itemGetProperty("wall") + 1)
            : 0

        itemSetProperty({wall})
    }

    const itemMoveToPrevWall = () => {
        const wall = (itemGetProperty("wall") - 1) >= 0
            ? (itemGetProperty("wall") - 1)
            : (getWalls().length - 1)

        itemSetProperty({wall})
    }

    const itemRotateClockwise = () => {
        let rotate = itemGetProperty("rotate") - 45

        if (rotate > 360) rotate = rotate - 360
        else if (rotate < 0) rotate = rotate + 360
        else if (rotate === 360) rotate = 0

        itemSetProperty({rotate})
    }

    const itemRotateAnticlockwise = () => {
        let rotate = itemGetProperty("rotate") + 45

        if (rotate > 360) rotate = rotate - 360
        else if (rotate < 0) rotate = rotate + 360
        else if (rotate === 360) rotate = 0

        itemSetProperty({rotate})
    }

    const itemMirrorVertical = () => {
        const scale = {...itemGetProperty("scale")}
        scale.x = (scale.x || 1) * -1

        itemSetProperty({scale})
    }

    const itemMirrorHorizontal = () => {
        const scale = {...itemGetProperty("scale")}
        scale.z = (scale.z || 1) * -1

        itemSetProperty({scale})
    }

    const itemRemove = () => {
        setStateAndSave({interiors: state.getCurrent().interiors.filter(item => item.id !== getHandle())})
        unsetHandle()
    }

    const itemCopy = () => {
        const item = JSON.parse(JSON.stringify(itemGet()))

        item.id = uuidv4()
        item.position.x = null
        item.position.z = null

        setStateAndSave({interiors: [...state.getCurrent().interiors, item]})
        setHandle(item.id)
    }

    const windowMethods = {
        itemIsWindow: () => {
            return itemGetProperty("type") === "window"
        },

        itemSetWindowType: (windowType) => {
            return itemSetProperty({windowType})
        },

        itemGetWindowType: () => {
            return itemGetProperty("windowType")
        }
    }

    const doorMethods = {
        itemIsDoor: () => {
            return itemGetProperty("type") === "door"
        },

        itemIsMountingSurface: () => {
            return itemGetProperty("mountingSurface")
        },

        itemToggleMountingSurface: () => {
            return itemSetProperty({mountingSurface: !itemGetProperty("mountingSurface")})
        },

        itemGetDoorHandlePosition: () => {
            return itemGetProperty("doorHandlePosition")
        },

        itemSetDoorHandlePosition: (doorHandlePosition) => {
            return itemSetProperty({doorHandlePosition})
        },

        itemGetDoorOpeningDirection: () => {
            return itemGetProperty("doorOpeningDirection")
        },

        itemSetDoorOpeningDirection: (doorOpeningDirection) => {
            return itemSetProperty({doorOpeningDirection})
        },

        itemGetDoorType: () => {
            return itemGetProperty("doorType")
        },

        itemSetDoorType: (doorType) => {
            return itemSetProperty({doorType})
        }
    }

    const validate = (currentStep) => {
        if (["wall", "floor", "ceiling"].includes(currentStep)) {
            setStateAndSave({interiors: removeEmptyUserInputInteriors(state.getCurrent().interiors)})
        }

        try {
            if (currentStep === "shape") {
                const dimensions = getDimensions();
                Object.keys(dimensions).map(dimension => {
                    if (!dimensions[dimension] || dimensions[dimension] <= 0) {
                        if (dimension === "height") throw Error(local.__("Raumhöhe muss ein Wert größer 0 haben"))
                        throw Error(local.__("Länge %dimension% muss Wert größer 0 haben", {dimension: dimension.toUpperCase()}))
                    }
                    if (dimensions[dimension] > roomSetupShapeDimensionMax) {
                        if (dimension === "height") throw Error(local.__("Raumhöhe darf %max% nicht überschreiten", {dimension: dimension.toUpperCase(), max: roomSetupShapeDimensionMax}))
                        throw Error(local.__("Länge %dimension% darf %max% nicht überschreiten", {dimension: dimension.toUpperCase(), max: roomSetupShapeDimensionMax}))
                    }
                })

                if (getShape() === "l_form_a" && dimensions["a"] >= dimensions["c"]) throw Error(local.__("Länge A muss kleiner C sein"))
                if (getShape() === "l_form_a" && dimensions["b"] >= dimensions["d"]) throw Error(local.__("Länge B muss kleiner D sein"))
                if (getShape() === "l_form_b" && dimensions["a"] >= dimensions["c"]) throw Error(local.__("Länge A muss kleiner C sein"))
                if (getShape() === "l_form_b" && dimensions["d"] >= dimensions["b"]) throw Error(local.__("Länge D muss kleiner B sein"))
                if (getShape() === "l_form_c" && dimensions["b"] >= dimensions["d"]) throw Error(local.__("Länge B muss kleiner D sein"))
                if (getShape() === "l_form_c" && dimensions["c"] >= dimensions["a"]) throw Error(local.__("Länge C muss kleiner A sein"))
                if (getShape() === "l_form_d" && dimensions["c"] >= dimensions["a"]) throw Error(local.__("Länge C muss kleiner A sein"))
                if (getShape() === "l_form_d" && dimensions["d"] >= dimensions["b"]) throw Error(local.__("Länge D muss kleiner B sein"))
                if (getShape() === "pentagonal_a" && dimensions["a"] >= dimensions["c"]) throw Error(local.__("Länge A muss kleiner C sein"))
                if (getShape() === "pentagonal_a" && dimensions["b"] >= dimensions["d"]) throw Error(local.__("Länge B muss kleiner D sein"))
                if (getShape() === "pentagonal_b" && dimensions["a"] >= dimensions["c"]) throw Error(local.__("Länge A muss kleiner C sein"))
                if (getShape() === "pentagonal_b" && dimensions["d"] >= dimensions["b"]) throw Error(local.__("Länge D muss kleiner B sein"))
                if (getShape() === "pentagonal_c" && dimensions["c"] >= dimensions["a"]) throw Error(local.__("Länge C muss kleiner A sein"))
                if (getShape() === "pentagonal_c" && dimensions["b"] >= dimensions["d"]) throw Error(local.__("Länge D muss kleiner B sein"))
                if (getShape() === "pentagonal_d" && dimensions["c"] >= dimensions["a"]) throw Error(local.__("Länge A muss kleiner C sein"))
                if (getShape() === "pentagonal_d" && dimensions["d"] >= dimensions["b"]) throw Error(local.__("Länge D muss kleiner B sein"))

                if (!getCeilingConstitution()) throw Error(local.__("Bitte wählen sie eine Option für Deckenbeschaffenheit."))
                if (!getWallConstitution()) throw Error(local.__("Bitte wählen sie eine Option für Wandbeschaffenheit."))
                if (!getFloorConstitution()) throw Error(local.__("Bitte wählen sie eine Option für Bodenbeschaffenheit."))
            }

            return true
        } catch (error) {
            messenger.showError(local.__(error.message))
        }
        return false
    }

    return {
        save, reset,
        getId,
        setRoom,
        getType, setType,
        getShape, setShape,
        hasDimensions, getDimensions, setDimensions, setDimension, getWalls,
        isInteriorBasicMode, unsetInteriorBasicMode, setInteriorBasicMode,
        isCeilingUsable, toggleCeilingUsable,
        getCeilingConstitution, setCeilingConstitution,
        getWallConstitution, setWallConstitution,
        getFloorConstitution, setFloorConstitution,
        getNoises, hasNoise, toggleNoise,
        getImpressions, hasImpression, toggleImpression,
        getFireProtectionClass, setFireProtectionClass,
        isCustomBudget, setCustomBudget, unsetCustomBudget, getBudget, setBudget,
        getInteriors,
        getRoomHeight,
        addInterior,

        hasHandle, getHandle, itemGet, setHandle, unsetHandle,

        itemGetProperty, itemSetProperty,
        itemAllowUserInput, itemSetPosition, itemSetSize, itemSetWidth, itemSetHeight, itemSetDepth, itemSetOffset, itemMoveToNextWall, itemMoveToPrevWall, itemRotateClockwise, itemRotateAnticlockwise, itemMirrorVertical, itemMirrorHorizontal, itemRemove, itemCopy,
        ...windowMethods, ...doorMethods,
        validate,
        hasDiff
    }
}

export const apiDataToRoomSetup = (apiData, roomInteriors) => {
    const solutionInteriors = apiData.interior.map(solutionInterior => {
        const roomInterior = JSON.parse(JSON.stringify(roomInteriors.find(roomInterior => roomInterior.id === solutionInterior.interiorId)))

        var convertedpos = {x: solutionInterior.position?.z, y: solutionInterior.position?.y, z: solutionInterior.position?.x}
        if (roomInterior.type == "furniture" || roomInterior.type == "window" || roomInterior.type == "door") {
            convertedpos.y = convertedpos.y - solutionInterior.size.y / 2
        }
        var convertedsize = {
            x: solutionInterior.size.x * (solutionInterior.mirror?.x || 1),
            y: solutionInterior.size.y * (solutionInterior.mirror?.y || 1),
            z: solutionInterior.size.z * (solutionInterior.mirror?.z || 1),
        }

        if (roomInterior.type == "window" || roomInterior.type == "door") {
            var t = convertedsize.z;
            convertedsize.z = convertedsize.x;
            convertedsize.x = t;
        }

        roomInterior.original = roomInterior.id
        roomInterior.id = uuidv4()
        roomInterior.position = convertedpos //solutionInterior.position
        roomInterior.width = Math.abs(convertedsize.z) //Math.abs(solutionInterior.size.x)
        roomInterior.height = Math.abs(convertedsize.y)// Math.abs(solutionInterior.size.y)
        roomInterior.depth = Math.abs(convertedsize.x) // Math.abs(solutionInterior.size.z)
        roomInterior.rotate = solutionInterior.rotate
        roomInterior.wall = solutionInterior.wall
        roomInterior.mirror = {
            x: solutionInterior.size.x > 0 ? 1 : -1,
            y: solutionInterior.size.y > 0 ? 1 : -1,
            z: solutionInterior.size.z > 0 ? 1 : -1
        }
        return roomInterior
    })

    return {
        id: apiData.id,
        type: apiData.typeCode,
        shape: apiData.layout.shape,
        dimensions: apiData.layout.dimensions,
        interiors: solutionInteriors,
        noiseSourceCodes: apiData.requirements.noiseSourceCodes,
        budget: apiData.requirements.budget,
        fireProtectionClassCode: apiData.requirements.fireProtectionClassCode,
        ceilingConstitution: apiData.structure.ceiling.code,
        wallConstitution: apiData.structure.wall.code,
        floorConstitution: apiData.structure.floor.code,
        preferredImpressions: apiData.requirements.preferredImpressions,
        useCeiling: apiData.requirements.allowCeilingMounting,
    }
}

const roomSetupApiData = (roomSetup) => {
    return {
        typeCode: roomSetup.type,
        layout: {
            shape: roomSetup.shape,
            dimensions: roomSetup.dimensions,
            walls: layoutToWalls(roomSetup.shape, roomSetup.dimensions).map(wall => {
                return {
                    base: [
                        {x: wall.p1.x, y: 0, z: wall.p1.z},
                        {x: wall.p2.x, y: 0, z: wall.p2.z},
                        {x: wall.p3.x, y: 0, z: wall.p3.z},
                        {x: wall.p4.x, y: 0, z: wall.p4.z}
                    ],
                    extrusion: {x: 0, y: wall.h, z: 0},
                }
            })
        },
        interior: roomSetup.interiors
            .filter(interior => interior.position.x !== null && interior.position.y !== null && interior.position.z !== null)
            .map(interior => {
                const mirror = interior.mirror || {x: 1, y: 1, z: 1}

                var convertedpos = {x: interior.position?.z, y: interior.position?.y, z: interior.position?.x}
                var convertedsize = {
                    x: interior.width * (mirror.x || 1),
                    y: interior.height * (mirror.y || 1),
                    z: interior.depth * (mirror.z || 1),
                }

                if (interior.type == "furniture" || interior.type == "window" || interior.type == "door") {
                    convertedpos.y = convertedpos.y + interior.height / 2
                }

                if (interior.type == "furniture" || interior.type == "lamp") {
                    convertedsize.x = interior.depth * (mirror.z || 1)
                    convertedsize.z = interior.width * (mirror.x || 1)
                }

                return {
                    interiorId: interior.original,
                    position: convertedpos || {x: 0, y: 0, z: 0},
                    size: convertedsize,
                    rotate: interior.rotate || 0,
                    wall: interior.wall || 0,
                }
            }),
        requirements: {
            fireProtectionClassCode: roomSetup.fireProtectionClass,
            noiseSourceCodes: roomSetup.noises,
            budget: parseInt(roomSetup.budget),
            preferredImpressions: roomSetup.preferredImpressions,
            allowCeilingMounting: roomSetup.useCeiling,
        },
        structure: {
            ceilingStructureCode: roomSetup.ceilingConstitution,
            wallStructureCode: roomSetup.wallConstitution,
            floorStructureCode: roomSetup.floorConstitution,
        }
    }
}

export const convertToInterior = (interiorItem) => {
    const interior = JSON.parse(JSON.stringify(interiorItem))

    interior.original = interiorItem.id
    interior.id = uuidv4()
    interior.wall = 0
    interior.scale = {x: 1, y: 1, z: 1}
    interior.position = {x: null, y: 0, z: null}
    interior.rotate = 0

    return interior
}

export const removeEmptyUserInputInteriors = (interiors) => {
    return interiors.filter(interior => {
        if (interior.userInput.includes("width") && !interior.width) return false
        if (interior.userInput.includes("depth") && !interior.depth) return false
        if (interior.userInput.includes("height") && !interior.height) return false
        if (interior.userInput.includes("floorOffset") && interior.position.y === "") return false
        return true
    })
}